import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardSubtitle,
  CardTitle,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { checkarrowIcon } from "../../constant/icons";
import { useApi } from "../../helper/apiHook";
import { message } from "../../components/toast";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const planDescriptionValue = (plan: any, key: string) =>
  plan?.description?.[key] ?? plan?.description?.[`${key} `];

const SubscriptionView = (props: any) => {
  const { PlanToggle, subscription, companyId, tenantId, onSubscribed, hideClose } = props;
  const plansApi = useApi("payment", false, tenantId ? { tenantId } : {});
  const paymentApi = useApi("payment", false, {}, {}, true);
  const [busyPlanId, setBusyPlanId] = useState<number | null>(null);

  const payload = Array.isArray(plansApi?.data) ? plansApi.data[0] : plansApi?.data;
  const plans = payload?.plans || [];
  const currency = payload?.currency || "USD";
  const currencySymbol = payload?.currencySymbol || currency;

  useEffect(() => {
    if (hideClose && payload?.isSubscribe) {
      onSubscribed?.();
    }
  }, [hideClose, payload?.isSubscribe, onSubscribed]);

  const finishSubscribe = () => {
    onSubscribed?.();
    PlanToggle?.();
  };

  const openCheckout = (order: any) => {
    if (!window.Razorpay) {
      message("error", "Unable to load Razorpay checkout");
      return;
    }
    const razorpay = new window.Razorpay({
      key: order.razorpayKeyId || payload?.razorpayKeyId,
      subscription_id: order.subscriptionId,
      name: "MMC Excel Converter",
      description: order.plan?.name || "Xero subscription (auto-renews monthly)",
      prefill: order.prefill || {},
      retry: {
        enabled: true,
        max_count: 3,
      },
      handler: async (response: any) => {
        const verifyResult = await paymentApi.serverRequest({
          url: "/payment/verify",
          requestType: "POST",
          isMutate: false,
          body: {
            tenantId,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          },
        });
        if (verifyResult?.status) {
          finishSubscribe();
        }
      },
      modal: {
        ondismiss: () => {
          message("error", "Payment cancelled");
        },
      },
    });
    razorpay.on("payment.failed", (response: any) => {
      const description = response?.error?.description || "Payment failed";
      message("error", description);
    });
    razorpay.open();
  };

  const handleBtnClick = async (plan: any) => {
    if (!tenantId) {
      message("error", "Organisation not found. Connect Xero first.");
      return;
    }
    setBusyPlanId(plan.id);
    try {
      if (plan.isTrial || plan.amount === 0) {
        const subscribe = await paymentApi.serverRequest({
          url: "/payment",
          requestType: "POST",
          isMutate: false,
          body: { planId: plan.id, tenantId, companyId },
        });
        if (subscribe?.status) {
          finishSubscribe();
        }
        return;
      }

      const order = await paymentApi.serverRequest({
        url: "/payment/order",
        requestType: "POST",
        isMutate: false,
        body: { planId: plan.id, tenantId, companyId },
      });
      if (!order?.status) {
        return;
      }
      const result = order.data?.[0];
      if (result?.subscriptionId) {
        openCheckout(result);
      }
    } finally {
      setBusyPlanId(null);
    }
  };

  return (
    <Modal
      className="subscription-modal"
      toggle={hideClose ? undefined : PlanToggle}
      isOpen={subscription}
      size="xl"
      centered
      backdrop="static"
      returnFocusAfterClose={false}
      keyboard={!hideClose}
    >
      {!hideClose && <ModalHeader toggle={PlanToggle} />}
      <ModalBody>
        <div className="subscription-header">
          <h4>Choose a Xero plan</h4>
          <p>Your organisation is not subscribed. Select Trial or 30 Days to continue.</p>
        </div>
        <div className="subscription-list">
          {plans.map((plan: any) => {
            const isTrial = plan.isTrial || plan.amount === 0;
            const eligible = plan.isEligible !== false;
            return (
              <Card key={plan.id} className={isTrial ? "" : "plan-highlight"}>
                <CardBody>
                  {!isTrial && <span className="plan-badge">Most Popular</span>}
                  <CardTitle tag="h5">{plan.name}</CardTitle>
                  <CardSubtitle tag="h6">
                    {isTrial
                      ? "Try Excel to Xero free for 7 days and automate your Excel-to-Xero data transfers with ease."
                      : "Get ongoing access to Excel to Xero and simplify your Excel-to-Xero workflow with automated data transfers."}
                  </CardSubtitle>
                  <div>
                    <h2>
                      {isTrial
                        ? "Free"
                        : `${plan.currencySymbol || currencySymbol} ${plan.amount}`}
                      <span>{isTrial ? `/ ${plan.validity} Days` : "/ month"}</span>
                    </h2>
                  </div>
                  <Button
                    color="primary"
                    disabled={!eligible || busyPlanId === plan.id}
                    onClick={() => handleBtnClick(plan)}
                  >
                    {!eligible
                      ? "Trial already used"
                      : busyPlanId === plan.id
                        ? "Please wait..."
                        : isTrial
                          ? "Start Free Trial"
                          : "Subscribe"}
                  </Button>
                  <ul>
                    <li>
                      <span>{checkarrowIcon.icon}</span>
                      <p>Organisation Limit <b>{planDescriptionValue(plan, "organisationLimit")}</b></p>
                    </li>
                    <li>
                      <span>{checkarrowIcon.icon}</span>
                      <p>User Limit <b>{planDescriptionValue(plan, "userLimit")}</b></p>
                    </li>
                    <li>
                      <span>{checkarrowIcon.icon}</span>
                      <p>Transactional Line Limit <b>{planDescriptionValue(plan, "transactionalLineLimit")}</b></p>
                    </li>
                    <li>
                      <span>{checkarrowIcon.icon}</span>
                      <p>Support on <b>{planDescriptionValue(plan, "support")}</b></p>
                    </li>
                    <li>
                      <span>{checkarrowIcon.icon}</span>
                      <p>Credit Card Requirement <b>{planDescriptionValue(plan, "creditCardRequirement")}</b></p>
                    </li>
                  </ul>
                </CardBody>
              </Card>
            );
          })}
        </div>
      </ModalBody>
    </Modal>
  );
};

export { SubscriptionView };
